import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import { BuildSearchParams } from "~/components/build/Show/lib/useQueryParams";

export function urlForView(buildPath: string, view: BuildView, params?: BuildSearchParams): string {
  const views = {
    [BuildView.Summary]: "summary",
    [BuildView.Steps]: "steps",
    [BuildView.Canvas]: "steps/canvas",
    [BuildView.Table]: "steps/table",
    [BuildView.Waterfall]: "steps/waterfall",
    [BuildView.Tests]: "tests",
    [BuildView.Annotations]: "annotations",
  };

  if (!views[view]) {
    throw new Error(`Unknown view: ${view}`);
  }

  const pathname = `${buildPath}/${views[view]}`;

  if (params) {
    return applySearchParams(pathname, params);
  }

  return pathname;
}

export function applySearchParams(path, params: BuildSearchParams) {
  const searchParams = new URLSearchParams();
  if (params.jobUuid) {
    searchParams.set("jid", params.jobUuid);
  }
  if (params.stepUuid) {
    searchParams.set("sid", params.stepUuid);
  }
  if (params.open === "false") {
    searchParams.set("open", `${params.open}`);
  }
  if (params.tab && Features.BuildJobTabUrls) {
    searchParams.set("tab", params.tab);
  }

  return `${path}?${searchParams.toString()}`;
}

/**
 * Resolve a job URL based on the current location or a custom pathname.
 *
 * @param jobUuid - The UUID of the job to resolve.
 * @param options - Options for resolving the URL.
 * @param options.pathname - The base path to use for the URL. Defaults to the current window location.
 * @param options.open - Whether to open the drawer for the job. Defaults to true.
 */
export function resolveJobUrl(
  jobUuid: string,
  { pathname = window.location.pathname, open = true } = {},
) {
  return applySearchParams(pathname, {
    jobUuid,
    open: open === false ? "false" : "true",
  });
}

/**
 * Resolve a step URL based on the current location or a custom pathname.
 *
 * @param stepUuid - The UUID of the step to resolve.
 * @param options - Options for resolving the URL.
 * @param options.pathname - The base path to use for the URL. Defaults to the current window location.
 * @param options.open - Whether to open the drawer for the step. Defaults to true.
 */
export function resolveStepUrl(
  stepUuid: string,
  { pathname = window.location.pathname, open = true } = {},
) {
  return applySearchParams(pathname, {
    stepUuid,
    open: open === false ? "false" : "true",
  });
}
